import { useState } from 'react';
import { MoreHorizontal, Pencil, PauseCircle, Trash2, RotateCcw } from 'lucide-react';
import {
  Badge,
  Button,
  Chip,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useMarkJourneyLost,
  useReengageJourney,
  useDeleteJourney,
  type CustomerJourney,
} from '@/api/customer-journey';
import { formatDate } from '@/lib/format';
import { saleFollowerNames } from '../sale-process-helpers';
import { errorMessage } from '../sale-process-error';
import { EditDealInfoDrawer } from '../edit-deal-info-drawer';
import type { JourneyOutcome } from './board-helpers';

type Dialog = 'pause' | 'reengage' | 'delete' | null;

/**
 * The board's journey header — journey label + sales owner + interested-service
 * chips on the left; Edit deal info and a ⋮ overflow (Pause / Re-engage /
 * Delete) on the right. Mirrors legacy JourneyHeader. Pause maps to the "mark
 * lost / on-hold" flow (reason required); Delete requires a reason too.
 */
export function JourneyBoardHeader({
  customerId,
  journey,
  outcome,
  isUpsell,
}: {
  customerId: number;
  journey: CustomerJourney;
  outcome: JourneyOutcome;
  isUpsell: boolean;
}) {
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [editing, setEditing] = useState(false);

  const markLost = useMarkJourneyLost(customerId, journey.customer_journey_id);
  const reengage = useReengageJourney(customerId, journey.customer_journey_id);
  const del = useDeleteJourney(customerId, journey.customer_journey_id);

  const owner = saleFollowerNames(journey)[0] ?? 'Unassigned';
  const services = journey.customer_journey_explaination?.service_interestings ?? [];
  const closed = outcome !== 'active';

  const close = () => {
    setDialog(null);
    setReason('');
    setReasonError(false);
  };
  const onErr = (label: string) => (e: unknown) => toast.error(`Failed to ${label}`, { description: errorMessage(e) });
  const onOk = (label: string) => () => {
    toast.success(label);
    close();
  };

  const runPause = () => {
    if (!reason.trim()) {
      setReasonError(true);
      return;
    }
    markLost.mutate({ reason: reason.trim() }, { onSuccess: onOk('Journey paused'), onError: onErr('pause journey') });
  };
  const runReengage = () =>
    reengage.mutate({ note: reason.trim() }, { onSuccess: onOk('Journey re-engaged'), onError: onErr('re-engage') });
  const runDelete = () => {
    if (!reason.trim()) {
      setReasonError(true);
      return;
    }
    del.mutate(reason.trim(), { onSuccess: onOk('Journey deleted'), onError: onErr('delete journey') });
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--vn-space-3)', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)', flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
              Sales: {owner}
            </span>
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
              {formatDate(journey.customer_journey_created_at)}
            </span>
            {isUpsell ? (
              <Badge tone="info" appearance="subtle" size="sm">
                Upsell
              </Badge>
            ) : null}
          </div>
          {services.length ? (
            <div style={{ display: 'flex', gap: 'var(--vn-space-1)', flexWrap: 'wrap' }}>
              {services.map((s) => (
                <Chip key={s} size="sm" tone="primary">
                  {s}
                </Chip>
              ))}
            </div>
          ) : null}
        </div>

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
          {!closed ? (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              <Pencil size={14} style={{ marginRight: 'var(--vn-space-1)' }} /> Edit Sales / forecast
            </Button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton size="sm" variant="ghost" aria-label="Journey actions">
                <MoreHorizontal size={15} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!closed ? (
                <DropdownMenuItem onSelect={() => setDialog('pause')}>
                  <PauseCircle size={14} style={{ marginRight: 'var(--vn-space-2)' }} /> Pause
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => setDialog('reengage')}>
                  <RotateCcw size={14} style={{ marginRight: 'var(--vn-space-2)' }} /> Re-engage
                </DropdownMenuItem>
              )}
              <DropdownMenuItem tone="danger" onSelect={() => setDialog('delete')}>
                <Trash2 size={14} style={{ marginRight: 'var(--vn-space-2)' }} /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <ConfirmDialog
        open={dialog === 'pause'}
        onOpenChange={(o) => (o ? setDialog('pause') : close())}
        title="Pause journey"
        description="Put this deal on hold. Record why — you can re-engage it later."
        confirmLabel="Pause"
        loading={markLost.isPending}
        onConfirm={runPause}
      >
        <Field label="Reason" required error={reasonError ? 'A reason is required.' : undefined}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError(false);
              }}
              placeholder="Why is this journey being paused?"
            />
          )}
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'reengage'}
        onOpenChange={(o) => (o ? setDialog('reengage') : close())}
        title="Re-engage journey"
        description="Re-open this journey and resume the pipeline."
        confirmLabel="Re-engage"
        loading={reengage.isPending}
        onConfirm={runReengage}
      >
        <Field label="Note">
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this journey being re-engaged?"
            />
          )}
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'delete'}
        onOpenChange={(o) => (o ? setDialog('delete') : close())}
        title="Delete journey"
        description="Permanently delete this sales journey. This cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={runDelete}
      >
        <Field label="Reason" required error={reasonError ? 'A reason is required.' : undefined}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError(false);
              }}
              placeholder="Why is this journey being deleted?"
            />
          )}
        </Field>
      </ConfirmDialog>

      <EditDealInfoDrawer customerId={customerId} journey={journey} open={editing} onOpenChange={setEditing} />
    </>
  );
}

export default JourneyBoardHeader;
